import {
  isValidOpening,
  wallOpeningOpening2,
  wallOpeningOpening3,
  wallOpeningPlane,
} from "building-common/openings/openingQuery";
import { profile2OrientCopy } from "building-common/profile2Utils";
import { FinishClipping } from "building-types/casing/casingTypes";
import type { OpeningTrimType } from "building-types/casing/openingTrimTypes";
import { doorOperationTypeIsOpen } from "building-types/doors/doorOperationTypes";
import type { DoorUnit, GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { OpeningSideHolePlacement } from "building-types/openings/opening";
import type { OpeningSideName } from "building-types/openings/openingTypes";
import { OpeningWrappingModeEnum } from "building-types/openings/openingWrappingModes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import type { Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { floorAssemblyBaseOffset } from "doc/elements/floor/floorAssembly";
import { roomIsEffectivelyExterior } from "doc/rooms/roomQuery";
import { isExteriorDoorCasingSettings } from "doc/settingsTools";
import { DIST_EPSILON } from "geom/core";
import { polyLine2Copy, polyLine2SetCCW } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { apronCasingEnabled, headCasingEnabled, sideCasingEnabled } from "../casing/casingQuery";
import { checkOrientationWithMode } from "../streetOrientation";
import {
  doorDescriptorHasCasing,
  doorDescriptorHasInteriorCasing,
  doorDescriptorHasWrapping,
} from "./doorDescriptorQuery";
import { doorOperationHasCasing } from "./doorOperation";

export function doorHasCasing(
  door: GeneratedDoorPlacement,
  casingSettings: ExteriorDoorCasingSettings | InteriorDoorCasingSettings,
): boolean {
  if (!casingSettings) {
    return false;
  }
  if (isExteriorDoorCasingSettings(casingSettings)) {
    return doorHasExteriorCasing(door, casingSettings);
  }
  return doorDescriptorHasInteriorCasing(door.descriptor);
}

export function doorHasWrapping(door: Pick<GeneratedDoorPlacement, "wrapping" | "descriptor">): boolean {
  // @todo(Andrew): this should be per side
  if (!!door.wrapping) {
    return door.wrapping.wrappingMode !== OpeningWrappingModeEnum.NONE;
  }
  return doorDescriptorHasWrapping(door.descriptor);
}

export function doorHasExteriorCasing(
  door: Pick<GeneratedDoorPlacement, "streetOrientation" | "descriptor">,
  exteriorCasing: ExteriorDoorCasingSettings | null,
): boolean {
  if (door.descriptor && !doorOperationHasCasing(door.descriptor.operationType)) {
    return false;
  }
  if (
    exteriorCasing?.casingOrientation &&
    !checkOrientationWithMode(door.streetOrientation, exteriorCasing.casingOrientation)
  ) {
    return false;
  }
  return (
    !!exteriorCasing &&
    doorDescriptorHasCasing(door.descriptor) &&
    (sideCasingEnabled(exteriorCasing.sideCasingType) || headCasingEnabled(exteriorCasing.headCasingType))
  );
}

export function doorCasingClipsFinish(
  door: GeneratedDoorPlacement,
  exteriorCasing: ExteriorDoorCasingSettings | null,
): boolean {
  if (!doorHasExteriorCasing(door, exteriorCasing)) {
    return false;
  }
  return doorSideCasingClipsFinish(exteriorCasing) || doorHeadCasingClipsFinish(exteriorCasing);
}

export function doorSideCasingClipsFinish(exteriorCasing: ExteriorDoorCasingSettings | null): boolean {
  if (!exteriorCasing) {
    return false;
  }
  return (
    sideCasingEnabled(exteriorCasing.sideCasingType) &&
    exteriorCasing?.sideCasingFinishClipping === FinishClipping.CLIP_FINISH
  );
}

export function doorHeadCasingClipsFinish(exteriorCasing: ExteriorDoorCasingSettings | null): boolean {
  if (!exteriorCasing) {
    return false;
  }
  return (
    headCasingEnabled(exteriorCasing.headCasingType) &&
    exteriorCasing?.headCasingFinishClipping === FinishClipping.CLIP_FINISH
  );
}

export function doorApronCasingClipsFinish(exteriorCasing: ExteriorDoorCasingSettings | null): boolean {
  if (!exteriorCasing) {
    return false;
  }
  return (
    apronCasingEnabled(exteriorCasing.apronCasingType) &&
    exteriorCasing.apronCasingFinishClipping === FinishClipping.CLIP_FINISH
  );
}

export function doorTrimClipsExteriorFinish(
  door: Pick<DoorUnit, "exteriorCasingSettings" | "doorPlacement">,
  trimType: OpeningTrimType,
): boolean {
  const exteriorCasing =
    door.exteriorCasingSettings ??
    (door.doorPlacement.exteriorCasing as ExteriorDoorCasingSettings | undefined) ??
    null;

  switch (trimType) {
    case "HEAD_CASING":
    case "KEYSTONE":
      return doorHeadCasingClipsFinish(exteriorCasing);
    case "SIDE_CASING":
      return doorSideCasingClipsFinish(exteriorCasing);
    case "APRON":
      return doorApronCasingClipsFinish(exteriorCasing);
    case "STOOL":
      return false;
  }
}

// @todo(Andrew): move this to openingQuery.ts and generalize
export function placementIsExterior({ room }: Pick<OpeningSideHolePlacement, "room">): boolean {
  if (!room) {
    return true;
  }
  // roofIsEffectivelyExterior does not identify PORTICO rooms as exterior
  // for some reason, so we need to check for them explicitly
  if (room.type === RoomType.PORTICO) {
    return true;
  }
  /**
   * @TODO - use second parameter here and remove the PORTICO check
   */
  return roomIsEffectivelyExterior(room, false);
}

export function doorHasJamb(door: Pick<GeneratedDoorPlacement, "descriptor">) {
  return !doorOperationTypeIsOpen[door.descriptor.operationType];
}

type OpeningQueryDoor = GeneratedDoorPlacement;

export function doorOpening2(
  door: OpeningQueryDoor,
  side: OpeningSideName,
  includeCasing: boolean = true,
): PolyLine2 | undefined {
  if (!isValidDoor(door)) {
    return undefined;
  }
  if (!isValidOpening(door.opening)) {
    return undefined;
  }
  const op = wallOpeningOpening2(door.opening, side, !!door.hasExteriorCasing && includeCasing);
  // @TODO(Andrew): all profiles should be oriented and CCW - this code should be moved to inside of wallOpeningOpening2
  return profile2OrientCopy(polyLine2SetCCW(polyLine2Copy(op)));
}

export function doorFrontOpening2(door: OpeningQueryDoor, includeCasing: boolean = true): PolyLine2 | undefined {
  if (!isValidDoor(door)) {
    return undefined;
  }
  if (!isValidOpening(door.opening)) {
    return undefined;
  }
  const op = wallOpeningOpening2(door.opening, "FRONT", !!door.hasExteriorCasing && includeCasing);
  // @TODO(Andrew): all profiles should be oriented and CCW - this code should be moved to inside of wallOpeningOpening2
  return profile2OrientCopy(polyLine2SetCCW(polyLine2Copy(op)));
}

export function doorBackOpening2(door: OpeningQueryDoor, includeCasing: boolean = true): PolyLine2 | undefined {
  if (!isValidDoor(door)) {
    return undefined;
  }
  if (!isValidOpening(door.opening)) {
    return undefined;
  }
  return wallOpeningOpening2(door.opening, "BACK", !!door.hasExteriorCasing && includeCasing);
}

export function doorFrontOpening3(door: OpeningQueryDoor, includeCasing: boolean = true): PolyLine3 | undefined {
  if (!isValidDoor(door)) {
    return undefined;
  }
  if (!isValidOpening(door.opening)) {
    return undefined;
  }
  return wallOpeningOpening3(door.opening, "FRONT", !!door.hasExteriorCasing && includeCasing);
}

export function doorBackOpening3(door: OpeningQueryDoor, includeCasing: boolean = true): PolyLine3 | undefined {
  if (!isValidDoor(door)) {
    return undefined;
  }
  if (!isValidOpening(door.opening)) {
    return undefined;
  }
  return wallOpeningOpening3(door.opening, "BACK", !!door.hasExteriorCasing && includeCasing);
}

/**
 * Vertical extents of a door's opening, relative to the level floor. Mirrors the bottom and
 * height a door contributes to a wall gap, so cut plane checks and wall gaps agree.
 */
function doorOpeningVerticalExtents(
  doorPlacement: GeneratedDoorPlacement,
  roomFloorAssemblies?: Table<FloorAssembly>,
): { bottom: number; top: number } {
  const { descriptor, verticalOffset = 0, frontRoom } = doorPlacement;
  const floorAssembly = frontRoom ? roomFloorAssemblies?.[frontRoom.id] : undefined;
  const offset = verticalOffset + (floorAssembly ? floorAssemblyBaseOffset(floorAssembly) : 0);

  return { bottom: descriptor.zOffset + offset, top: descriptor.height + offset };
}

/**
 * Whether a door's opening is cut by the given cut plane. Doors that sit entirely below the
 * plane (e.g. a low cased opening) or entirely above it leave the wall solid where they are,
 * so they must not be drawn in plan and must not gap the wall cross section.
 *
 * An undefined cut plane means "no cut plane filtering", i.e. every door counts.
 */
export function doorIntersectsCutPlane(
  doorPlacement: GeneratedDoorPlacement,
  cutPlaneHeight: LevelInches | undefined,
  roomFloorAssemblies?: Table<FloorAssembly>,
): boolean {
  if (!doorPlacement.opening) {
    return false;
  }
  if (cutPlaneHeight === undefined) {
    return true;
  }

  const { bottom, top } = doorOpeningVerticalExtents(doorPlacement, roomFloorAssemblies);

  return bottom - DIST_EPSILON < cutPlaneHeight && top + DIST_EPSILON > cutPlaneHeight;
}

export function doorBackPlane(door: OpeningQueryDoor) {
  if (!isValidDoor(door)) {
    return undefined;
  }
  if (!isValidOpening(door.opening)) {
    return undefined;
  }
  return wallOpeningPlane(ensureDefined(door.opening), "BACK");
}

export function doorFrontPlane(door: OpeningQueryDoor) {
  if (!isValidDoor(door)) {
    return undefined;
  }
  if (!isValidOpening(door.opening)) {
    return undefined;
  }
  return wallOpeningPlane(ensureDefined(door.opening), "FRONT");
}

export function isValidDoor(door: OpeningQueryDoor | undefined): door is GeneratedDoorPlacement {
  if (!door) {
    return false;
  }
  return isValidOpening(door.opening);
}
